/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { AppstoreOutlined, TableOutlined } from '@ant-design/icons'
import type { ReactElement } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  font,
  weightBold,
  weightRegular,
  paragraphSmall,
} from 'src/components/css'
import type { ResultViewMode } from '../hooks/useResultViewMode'

interface Props {
  mode: ResultViewMode
  onChange: (mode: ResultViewMode) => void
}

interface Tab {
  value: ResultViewMode
  label: string
  icon: ReactElement
}

const TABS: Tab[] = [
  { value: 'card', label: 'Card', icon: <AppstoreOutlined /> },
  { value: 'table', label: 'List', icon: <TableOutlined /> },
]

/**
 * Card / List toggle for the results. Built in the same segmented-control
 * language as the game views' switcher (same 8px radius, same active fill) so
 * the two read as one widget used twice rather than two designs.
 */
const ResultViewSwitcher = ({ mode, onChange }: Props) => (
  <div role='group' aria-label='Results view' css={containerStyle}>
    {TABS.map((tab, index) => {
      const isActive = mode === tab.value
      return (
        <button
          key={tab.value}
          type='button'
          // Toggle buttons in a group, not tabs owning tabpanels — so
          // `aria-pressed` rather than `aria-selected`.
          aria-pressed={isActive}
          data-testid={`team-finder-view-${tab.value}`}
          onClick={() => onChange(tab.value)}
          css={tabStyle(isActive, index === 0, index === TABS.length - 1)}
        >
          <span aria-hidden='true'>{tab.icon}</span>
          {capitalizeWords(tab.label)}
        </button>
      )
    })}
  </div>
)

export default ResultViewSwitcher

// ─── Styles ──────────────────────────────────────────────────────────────────

const containerStyle = css`
  display: flex;
  align-items: center;
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  height: 40px;
`

const tabStyle = (isActive: boolean, isFirst: boolean, isLast: boolean) => css`
  ${font.body}
  ${paragraphSmall}
  ${isActive ? weightBold : weightRegular}
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 88px;
  height: 40px;
  padding: 0 12px;
  border-radius: ${isFirst ? '8px 0 0 8px' : isLast ? '0 8px 8px 0' : '0'};
  background-color: ${isActive ? colors.SECONDARY_300 : 'transparent'};
  border: none;
  cursor: pointer;
  line-height: 1;
  color: ${colors.NEUTRAL_0};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${isActive ? colors.SECONDARY_300 : colors.SECONDARY_200};
  }

  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: -2px;
  }
`
